import Config from './core/config'
import Session from './core/session'
import Queue from './core/queue'
import PageViewCollector from './collectors/pageview'
import InteractionCollector from './collectors/interactions'
import PerformanceCollector from './collectors/performance'
import ErrorCollector from './collectors/errors'
import APICollector from './collectors/api'
import StorageCollector from './collectors/storage'
import CookieCollector from './collectors/cookies'
import type { SDKConfig } from './types/events'

class RadarSnapSDK {
  private initialized: boolean = false

  /**
   * Initialize the SDK
   */
  public init(config: SDKConfig): void {
    if (this.initialized) {
      console.warn('[RadarSnap] SDK already initialized')
      return
    }

    try {
      // Initialize configuration
      Config.initialize(config)

      // Initialize session
      Session.initialize()

      if (Config.isDebug()) {
        console.log('[RadarSnap] SDK initialized', Session.getSessionInfo())
      }

      this.initialized = true

      // Start collectors (placeholder for next step)
      this.startCollectors()

      // Setup session end tracking
      this.setupSessionEndTracking()
    } catch (error) {
      console.error('[RadarSnap] Initialization failed:', error)
    }
  }

  /**
   * Setup session end tracking
   */
  private setupSessionEndTracking(): void {
    // Track session end before page unload
    window.addEventListener('beforeunload', () => {
      Session.trackSessionEnd()
      Queue.flush()
    })

    // Also track on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        Session.trackSessionEnd()
        Queue.flush()
      }
    })
  }

  /**
   * Identify user (after login)
   */
  public identify(userId: string): void {
    Session.identify(userId)
  }

  /**
   * Track custom event
   */
  public track(eventName: string, properties: Record<string, any> = {}): void {
    Session.trackEvent(eventName, properties)
  }

  /**
   * Get session info
   */
  public getSessionInfo() {
    return Session.getSessionInfo()
  }

  /**
   * Start all data collectors
   */
  private startCollectors(): void {
    if (Config.isDebug()) {
      console.log('[RadarSnap] Starting collectors...')
    }

    // Start page view tracking
    PageViewCollector.start()

    // Start interaction tracking (clicks, scrolls)
    InteractionCollector.start()

    // Start performance tracking
    PerformanceCollector.start()

    // Start error tracking
    ErrorCollector.start()

    // Start API call tracking (NEW)
    APICollector.start()

    // Start storage tracking (NEW)
    StorageCollector.start()

    // Analyze existing cookies (NEW)
    CookieCollector.analyzeCookies()
  }

  /**
   * Manually flush queue
   */
  public flush(): void {
    Queue.flush()
  }

  /**
   * Check if initialized
   */
  public isInitialized(): boolean {
    return this.initialized
  }

  /**
   * Track cookie consent
   */
  public consent(status: 'accepted' | 'rejected', categories?: {
    functional?: boolean
    analytics?: boolean
    marketing?: boolean
  }): void {
    CookieCollector.trackConsent({
      status,
      ...categories
    })
  }

  /**
   * Destroy SDK instance
   */
  public destroy(): void {
    Session.trackSessionEnd()
    Queue.flush()
    Queue.destroy()
    this.initialized = false
  }
}

// Create singleton instance
const sdk = new RadarSnapSDK()

// Auto-initialize if data-api-key attribute is present
if (typeof document !== 'undefined') {
  const script = document.currentScript as HTMLScriptElement
  if (script && script.dataset.apiKey) {
    sdk.init({
      apiKey: script.dataset.apiKey,
      debug: script.dataset.debug === 'true'
    })
  }
}

// Export for manual initialization
export default sdk

// Also attach to window for global access
if (typeof window !== 'undefined') {
  (window as any).RadarSnap = sdk
}